// Status — the event-day "mission control" (Devin 2026-11-02, after the ECRO
// dress rehearsal): today's competitions in syllabus order, type chips like
// the public results pages, and each competition's way through its whole
// tabulation (rounds, recall, results) with its next step. Pure helpers; the
// screen is OireachtasStatus.jsx. `row` is one competition of
// GET /oireachtas/:id/tabulation-status.
import { compareComps } from "../OireachtasPanel/compSort";
import { compStep, isMultiRound, placesApply, preRecallRounds, roundOpen, roundSigned, sheetState } from "./tabulationFlow";

// The public results pages' type chips, in Devin's order (2026-10-30; a copy
// of the web's src/Parent/Results/oireachtasResultsModel.js GROUPS).
export const TYPE_GROUPS = [
  ["Solo championships", ["solo_champ"]],
  ["Teams", ["team_2h", "team_3h", "team_4h", "team_6h", "team_8h"]],
  ["Premiere", ["premiere"]],
  ["Trad sets", ["trad_set"]],
  ["Treble reels", ["treble_reel"]],
  ["Figure and dance drama", ["figure", "dance_drama"]],
  ["Adults", ["adult_solo", "advanced_adult", "adult_team"]],
];
export const ALL = "All";
export const typeGroupOf = (type) => (TYPE_GROUPS.find(([, types]) => types.includes(type)) || ["Other"])[0];
// "All" first (mission control wants the whole day at a glance), then the
// groups present, in order
export const chipsOf = (rows) => {
  const present = new Set((rows || []).map((r) => typeGroupOf(r.competition.type)));
  return [ALL, ...[...TYPE_GROUPS.map(([g]) => g), "Other"].filter((g) => present.has(g))];
};
export const inChip = (row, chip) => !chip || chip === ALL || typeGroupOf(row.competition.type) === chip;

// Syllabus order (compareComps). It puts girls before boys inside Solo
// Championships by `gender`, which the status rows don't carry: the code's
// suffix says it (SC-11-G, SC-20-M).
const GENDER_BY_SUFFIX = { G: "girls", L: "ladies", B: "boys", M: "men" };
export const genderOfCode = (code) => GENDER_BY_SUFFIX[(String(code || "").match(/-([GLBM])$/) || [])[1]] || "";
export const sortRows = (rows) =>
  [...(rows || [])].sort((a, b) =>
    compareComps(
      { ...a.competition, gender: genderOfCode(a.competition.code) },
      { ...b.competition, gender: genderOfCode(b.competition.code) }
    )
  );

// Today's competitions (ids from tabulationFlow.scheduledToday; null = no
// schedule for today = everything).
export const todayRows = (rows, todayIds, showAll) =>
  !todayIds || showAll ? rows : rows.filter((r) => todayIds.has(String(r.competition._id)));

// ---- one round -------------------------------------------------------------------
const roundRowOf = (row, round) => (row.roundRows || []).find((r) => r.round === round);
// A round's sheets by View Scores' own state (tabulationFlow.sheetState, so
// the two screens agree — plan review 54cd343b R1-F3): entered = saved
// (entered, printed or signed off; a dancer left blank who did not dance
// still counts), printed = sign-off sheet out, signed = signed off.
export function roundCounts(row, round) {
  const slots = roundRowOf(row, round)?.slots || [];
  const st = slots.map((s) => sheetState(row, round, s.slot));
  return {
    seats: slots.length,
    entered: st.filter((x) => x !== "missing").length,
    printed: st.filter((x) => x === "printed").length,
    signed: st.filter((x) => x === "signed_off").length,
    started: st.some((x) => x !== "missing"),
  };
}
const roundDetail = (c) => {
  if (!c.started && !c.signed) return "—";
  const parts = [`${c.entered}/${c.seats} entered`];
  if (c.printed) parts.push(`${c.printed} printed`);
  if (c.signed) parts.push(`${c.signed} signed`);
  return parts.join(" · ");
};

// Red = waiting on the organiser (Devin 2026-11-03): sign-off sheets printed
// but not signed yet, or a recall / results checked and ready to publish.
// The rounds open now with printed, unsigned sheets → [{ round, printed }].
export function awaitingSignOff(row) {
  if (!row?.draw?.locked) return [];
  return (row.roundRows || [])
    .filter((rr) => roundOpen(row, rr.round) && !roundSigned(row, rr.round))
    .map((rr) => ({ round: rr.round, printed: roundCounts(row, rr.round).printed }))
    .filter((x) => x.printed > 0);
}
const roundsWord = (rounds) =>
  rounds.length === 1 ? `Round ${rounds[0]}` : `Rounds ${rounds.slice(0, -1).join(", ")} and ${rounds[rounds.length - 1]}`;

// ---- the progress track ------------------------------------------------------------
// [{ key, label, state: done|current|waiting, alert, detail, round? }]
// alert: the step waits on the organiser (printed sheets to sign, or checked
// and ready to publish)
// Multi-round: Round 1, Round 2, Recall, Round 3 (… any later rounds), Results.
// One round: Round 1, Results.
export function stepsOf(row) {
  const rounds = row.competition.rounds || 1;
  const step = compStep(row);
  const drawn = step.step !== "no_draw";
  const before = preRecallRounds(row);
  const steps = [];
  const roundStep = (r) => {
    const c = roundCounts(row, r);
    const recallRound = isMultiRound(row) && r > before.length;
    // each round from its own sheets (rounds 1 and 2 are typed side by
    // side — plan review 54cd343b R1-F2): done when all signed off, current
    // when any sheet is in or it is the next round to work on, else waiting;
    // a recall round waits for its recall
    let state = "waiting";
    if (!drawn || !roundOpen(row, r)) state = "waiting";
    else if (roundSigned(row, r)) state = "done";
    else if (c.started || (step.step === "scores" && step.round === r)) state = "current";
    let detail = roundDetail(c);
    if (recallRound && !row.recallPublished) detail = "after the recall";
    const ci = roundRowOf(row, r)?.checkIn;
    if (recallRound && row.recallPublished && ci?.taken) detail += ` · ${ci.checkedIn} checked in`;
    return { key: `r${r}`, label: `Round ${r}`, state, alert: state === "current" && c.printed > 0, detail, round: r };
  };
  const pubStep = (kind, label) => {
    const published = kind === "recall" ? row.recallPublished : row.resultsPublished;
    const corrected = !!row.corrected?.[kind];
    const checked = !!row.checks?.[kind]?.checked;
    const by = row.checks?.[kind]?.by;
    let state = "waiting";
    let detail = "";
    if (published && !corrected) {
      state = "done";
      detail = "Published";
    } else if (drawn && step.step === kind) {
      state = "current";
      if (published && corrected) detail = checked ? "Corrected — checked, publish again" : "Corrected — check, then publish again";
      else detail = checked ? (by ? `Checked by ${by}` : "Checked") : "Ready to check";
    } else if (published && corrected) {
      detail = "Corrected — publish again";
    }
    return { key: kind, label, state, alert: state === "current" && checked, detail };
  };
  // a one-round competition's places list, before its results (Devin 2026-11-04)
  const placesStep = () => {
    const corrected = !!row.corrected?.places && !!row.placesPublished && !row.resultsPublished;
    let state = "waiting";
    let detail = "";
    if (row.resultsPublished || (row.placesPublished && !corrected)) {
      state = "done";
      detail = row.placesPublished ? "Published" : "With the results";
    } else if (drawn && step.step === "results") {
      state = "current";
      detail = corrected ? "Corrected — publish again" : row.checks?.results?.checked ? "Checked — print, publish" : "After the results check";
    } else if (corrected) {
      detail = "Corrected — publish again";
    }
    return { key: "places", label: "Places", state, alert: false, detail };
  };
  before.forEach((r) => steps.push(roundStep(r)));
  if (isMultiRound(row)) {
    steps.push(pubStep("recall", "Recall"));
    for (let r = before.length + 1; r <= rounds; r++) steps.push(roundStep(r));
  }
  if (placesApply(row)) steps.push(placesStep());
  steps.push(pubStep("results", "Results"));
  return steps;
}

// ---- where it is now ----------------------------------------------------------------
// { short, line, tone: idle|busy|attn|done|warn }: `short` for the pill,
// `line` under the track
export function headlineOf(row) {
  const s = compStep(row);
  if (s.step === "no_draw") return { short: "Not drawn", line: "Draw not locked", tone: "idle" };
  const waiting = s.step === "done" ? [] : awaitingSignOff(row);
  if (waiting.length) {
    const n = waiting.reduce((t, x) => t + x.printed, 0);
    const line = `${roundsWord(waiting.map((x) => x.round))} — ${n} sign-off sheet${n === 1 ? "" : "s"} printed, not signed yet`; // no judge count: a judge may sit both rounds (code review 160cb260 R1-F1)
    return { short: "Awaiting sign-off", line: s.republish ? `${line} — corrected after publishing` : line, tone: "attn" };
  }
  if (s.step === "scores") {
    const c = roundCounts(row, s.round);
    const line = `Round ${s.round} — ${c.entered} of ${c.seats} sheets entered, ${c.signed} signed off`;
    return {
      short: `Round ${s.round}`,
      line: s.republish ? `${line} — corrected after publishing` : line,
      tone: s.republish ? "warn" : c.started || c.signed ? "busy" : "idle",
    };
  }
  if (s.step === "recall" || s.step === "results") {
    const checked = !!row.checks?.[s.step]?.checked;
    const what = s.step === "recall" ? "Recall" : "Results";
    if (checked) {
      if (s.step === "results" && s.republishPlaces) {
        return { short: "Places corrected", line: "Results checked — publish the places again", tone: "warn" };
      }
      if (s.step === "results" && placesApply(row) && row.placesPublished) {
        return { short: "Places published", line: "Places published — publish the results after the awards", tone: "attn" };
      }
      return {
        short: `${what} ready`,
        line: `${what} checked — ${s.republish ? "publish again" : "ready to publish"}`,
        tone: "attn",
      };
    }
    return {
      short: `Check ${what.toLowerCase()}`,
      line:
        s.step === "recall"
          ? "Rounds 1 and 2 signed off — check the recall in View Scores"
          : "Every sheet signed off — check the results in View Scores",
      tone: s.republish ? "warn" : "busy",
    };
  }
  return { short: "Published", line: "Results published", tone: "done" };
}

// ---- the next thing to press -----------------------------------------------------------
// "publish-recall" | "publish-results" | "open-scores" | "open-detail" | ""
// perms: { canPublish(kind), canScores, canDetail }
// A stage corrected after publishing is published again from View Scores, as
// today: the page's publish switch would UNpublish it (plan review 54cd343b
// R1-F1), so it gets no publish button here.
export function primaryActionOf(row, perms) {
  const s = compStep(row);
  for (const kind of ["recall", "results"]) {
    if (s.republish) break;
    const ready = kind === "recall" ? row.gate?.recallReady : row.gate?.resultsReady;
    if (s.step === kind && ready && row.checks?.[kind]?.checked && perms.canPublish(kind)) return `publish-${kind}`;
  }
  if (s.step === "no_draw" || s.step === "done") return perms.canDetail && s.step === "done" ? "open-detail" : "";
  if (perms.canScores) return "open-scores";
  if (perms.canDetail) return "open-detail";
  return "";
}

// ---- the shown list in numbers -----------------------------------------------------------
export function summaryOf(rows) {
  const out = { total: rows.length, notDrawn: 0, inProgress: 0, signOff: 0, ready: 0, published: 0 };
  for (const r of rows) {
    const s = compStep(r);
    if (s.step === "no_draw") out.notDrawn += 1;
    else if (s.step === "done") out.published += 1;
    // the headline's order: sheets to sign first (code review 160cb260 R1-F2)
    else if (awaitingSignOff(r).length) out.signOff += 1;
    else if ((s.step === "recall" || s.step === "results") && r.checks?.[s.step]?.checked) out.ready += 1;
    else out.inProgress += 1;
  }
  return out;
}
